import { getDayOfWeek } from "../../../utils/generalUtils";

/**
 * Creates props for patient acuity graph to be used with BlankCard component
 * @param {Array} data - Array of daily data objects containing patient acuity information
 * @param {Object} options - Additional options like height, chartHeight, etc.
 * @returns {Object} - Props object ready to spread into BlankCard component
 */
export const createPatientAcuity = (data, options = {}) => {
  // Set default values for options
  const {
    height = 350,
    chartHeight = 250,
    selectedDays = []
  } = options;

  // Map between full day names and abbreviated versions
  const dayAbbreviations = {
    Monday: "Mon",
    Tuesday: "Tue",
    Wednesday: "Wed",
    Thursday: "Thu",
    Friday: "Fri",
    Saturday: "Sat",
    Sunday: "Sun"
  };

  // Map abbreviated days back to full names for internal processing
  const getFullDayName = abbr => {
    const fullNames = {
      Mon: "Monday",
      Tue: "Tuesday",
      Wed: "Wednesday",
      Thu: "Thursday",
      Fri: "Friday",
      Sat: "Saturday",
      Sun: "Sunday"
    };
    return fullNames[abbr] || abbr;
  };

  // All possible weekdays in order
  const allWeekdays = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday"
  ];

  // Determine which days to show
  const daysToShow =
    selectedDays.length > 0 ? selectedDays.map(day => getFullDayName(day)) : allWeekdays;

  // Transform the data to show averages by weekday
  const transformDataByWeekday = () => {
    // Initialize counters for each weekday
    const weekdayCounts = {
      Sunday: { acuity1: 0, acuity2: 0, acuity3: 0, acuity4: 0, acuity5: 0, totalPatients: 0, totalDays: 0 },
      Monday: { acuity1: 0, acuity2: 0, acuity3: 0, acuity4: 0, acuity5: 0, totalPatients: 0, totalDays: 0 },
      Tuesday: { acuity1: 0, acuity2: 0, acuity3: 0, acuity4: 0, acuity5: 0, totalPatients: 0, totalDays: 0 },
      Wednesday: { acuity1: 0, acuity2: 0, acuity3: 0, acuity4: 0, acuity5: 0, totalPatients: 0, totalDays: 0 },
      Thursday: { acuity1: 0, acuity2: 0, acuity3: 0, acuity4: 0, acuity5: 0, totalPatients: 0, totalDays: 0 },
      Friday: { acuity1: 0, acuity2: 0, acuity3: 0, acuity4: 0, acuity5: 0, totalPatients: 0, totalDays: 0 },
      Saturday: { acuity1: 0, acuity2: 0, acuity3: 0, acuity4: 0, acuity5: 0, totalPatients: 0, totalDays: 0 }
    };

    // Keep track of which dates we've processed for day counting
    const processedDates = new Set();

    // Process each data point
    data.forEach(item => {
      const date = item.date;
      if (!date) return;

      // Skip if we've already counted this date
      if (processedDates.has(date)) return;

      // Get day of week
      const dayOfWeek = getDayOfWeek(date);

      // Add counts to the appropriate category
      if (item.acuity_load?.byAcuity) {
        if (item.acuity_load.byAcuity[1] !== undefined) {
          weekdayCounts[dayOfWeek].acuity1 += item.acuity_load.byAcuity[1];
          weekdayCounts[dayOfWeek].totalPatients += item.acuity_load.byAcuity[1];
        }
        if (item.acuity_load.byAcuity[2] !== undefined) {
          weekdayCounts[dayOfWeek].acuity2 += item.acuity_load.byAcuity[2];
          weekdayCounts[dayOfWeek].totalPatients += item.acuity_load.byAcuity[2];
        }
        if (item.acuity_load.byAcuity[3] !== undefined) {
          weekdayCounts[dayOfWeek].acuity3 += item.acuity_load.byAcuity[3];
          weekdayCounts[dayOfWeek].totalPatients += item.acuity_load.byAcuity[3];
        }
        if (item.acuity_load.byAcuity[4] !== undefined) {
          weekdayCounts[dayOfWeek].acuity4 += item.acuity_load.byAcuity[4];
          weekdayCounts[dayOfWeek].totalPatients += item.acuity_load.byAcuity[4];
        }
        if (item.acuity_load.byAcuity[5] !== undefined) {
          weekdayCounts[dayOfWeek].acuity5 += item.acuity_load.byAcuity[5];
          weekdayCounts[dayOfWeek].totalPatients += item.acuity_load.byAcuity[5];
        }
      }

      // Count this as a day for this weekday
      weekdayCounts[dayOfWeek].totalDays++;
      processedDates.add(date);
    });

    // Filter weekdays based on the selectedDays prop
    const orderedFilteredWeekdays = allWeekdays.filter(day => daysToShow.includes(day));

    // Calculate average acuity for each weekday
    const weekdayAcuities = {};
    let totalAcuitySum = 0;
    let totalPatientCount = 0;

    orderedFilteredWeekdays.forEach(day => {
      const counts = weekdayCounts[day];
      // Calculate average values by dividing by the number of days
      const divisor = counts.totalDays || 1;
      
      const acuity1 = Math.round(counts.acuity1 / divisor);
      const acuity2 = Math.round(counts.acuity2 / divisor);
      const acuity3 = Math.round(counts.acuity3 / divisor);
      const acuity4 = Math.round(counts.acuity4 / divisor);
      const acuity5 = Math.round(counts.acuity5 / divisor);
      
      const dailyPatients = acuity1 + acuity2 + acuity3 + acuity4 + acuity5;
      
      // Calculate weighted average acuity for this day
      const dailyAcuitySum = (acuity1 * 1) + (acuity2 * 2) + (acuity3 * 3) + (acuity4 * 4) + (acuity5 * 5);
      const avgAcuity = dailyPatients > 0 ? dailyAcuitySum / dailyPatients : 0;
      
      weekdayAcuities[day] = {
        avgAcuity: avgAcuity.toFixed(1),
        dailyPatients,
        day: dayAbbreviations[day]
      };
      
      totalAcuitySum += dailyAcuitySum;
      totalPatientCount += dailyPatients;
    });

    // Find overall average acuity
    const overallAvgAcuity = totalPatientCount > 0 ? (totalAcuitySum / totalPatientCount).toFixed(1) : "0.0";
    
    // Find day with highest and lowest acuity
    let highestAcuityDay = null;
    let lowestAcuityDay = null;
    let highestAcuity = -1;
    let lowestAcuity = 999;
    
    Object.entries(weekdayAcuities).forEach(([day, data]) => {
      const acuity = parseFloat(data.avgAcuity);
      if (acuity > highestAcuity && data.dailyPatients > 0) {
        highestAcuity = acuity;
        highestAcuityDay = day;
      }
      if (acuity < lowestAcuity && data.dailyPatients > 0) {
        lowestAcuity = acuity;
        lowestAcuityDay = day;
      }
    });
    
    // Calculate max difference in acuity between days
    const acuityVariance = (highestAcuity - lowestAcuity).toFixed(1);
    
    // Determine if variance is high or low (arbitrary threshold of 0.5)
    const varianceDescription = parseFloat(acuityVariance) > 0.3 ? 
      `high variance of ${acuityVariance} between days of the week` : 
      `insignificant changes in acuity and number of 1:1s between days of week `;
    
    // Create transformed data for chart
    const transformedData = orderedFilteredWeekdays.map(day => {
      const counts = weekdayCounts[day];
      const divisor = counts.totalDays || 1;

      return {
        dayOfWeek: dayAbbreviations[day], // Abbreviate to Mon, Tue, etc.
        acuity1: Math.round(counts.acuity1 / divisor),
        acuity2: Math.round(counts.acuity2 / divisor),
        acuity3: Math.round(counts.acuity3 / divisor),
        acuity4: Math.round(counts.acuity4 / divisor),
        acuity5: Math.round(counts.acuity5 / divisor)
      };
    });

    return {
      chartData: transformedData,
      overallAvgAcuity,
      highestAcuityDay: highestAcuityDay ? dayAbbreviations[highestAcuityDay] : null,
      lowestAcuityDay: lowestAcuityDay ? dayAbbreviations[lowestAcuityDay] : null,
      highestAcuity,
      lowestAcuity,
      acuityVariance,
      varianceDescription
    };
  };

  const {
    chartData,
    overallAvgAcuity,
    varianceDescription
  } = transformDataByWeekday();
  
  const colors = ["#5a77d6", "#6a4fcf", "#8535c4", "#a62699", "#8b0e3e"]; // From lowest to highest acuity

  return {
    title: "Patient Acuity Distribution",
    // description: `Average acuity: ${overallAvgAcuity}. ${varianceDescription}`,
    description: `Average acuity is <strong>${overallAvgAcuity}</strong>. There are ${varianceDescription}`,
    chartType: "stacked-bar",
    chartData: chartData,
    chartColors: colors,
    chartHeight,
    height,
    series: [
      { name: "Acuity 1", key: "acuity1" },
      { name: "Acuity 2", key: "acuity2" },
      { name: "Acuity 3", key: "acuity3" },
      { name: "Acuity 4", key: "acuity4" },
      { name: "Acuity 5", key: "acuity5" }
    ],
    xAxisKey: "dayOfWeek",
    xAxisLabel: "Day of Week",
    yAxisLabel: "Average Patient Count",
    status: "neutral", // No status
    legend: [
      { label: "1", color: colors[0] },
      { label: "2", color: colors[1] },
      { label: "3", color: colors[2] },
      { label: "4", color: colors[3] },
      { label: "5", color: colors[4] }
    ]
  };
};

export default createPatientAcuity; 